import { useEffect, useRef } from "react";
import {
  Alert,
  Button,
  Skeleton,
  VideoViewer,
} from "@codelittinc/carbon-design-system";
import { trackApp } from "../lib/analytics";
import { NOT_SAVED, SIGN_IN_TO_SAVE, VIDEO_FAILED, type VideoControls } from "../lib/video";
import { ACTION, Unavailable, type FocusRef } from "./Panels";

/**
 * An uploaded training video in the watch view. The player streams from the
 * bucket's presigned URL; a refreshed URL for the same material keeps the
 * position (`videoKey`), and closing reports what was watched.
 */
export function Video({ video, onEnded }: { video: VideoControls; onEnded: () => void }) {
  const { view, item } = video;
  const focusTarget = useRef<HTMLElement | null>(null);
  const focusRef: FocusRef = (element) => {
    focusTarget.current = element;
  };

  useEffect(() => {
    if (view.kind === "failed" && !view.retrying) focusTarget.current?.focus();
  }, [view]);

  return (
    <div>
      {video.notice === "signin" ? (
        <Alert variant="warning" title={SIGN_IN_TO_SAVE} className="mb-3">
          <Button variant="outline" className={`mt-2 ${ACTION}`} onClick={() => { trackApp("signin_again_clicked", { area: "video" }); video.signIn(); }}>
            Sign in again
          </Button>
        </Alert>
      ) : video.notice === "not_saved" ? (
        <Alert variant="warning" className="mb-3">
          {NOT_SAVED}
        </Alert>
      ) : null}
      {view.kind === "loading" ? (
        <>
          <p role="status" className="sr-only">
            Loading the video
          </p>
          <Skeleton className="aspect-video w-full rounded-lg motion-reduce:animate-none" />
        </>
      ) : null}
      {view.kind === "playing" && item ? (
        <VideoViewer
          videoKey={item.materialId}
          src={view.playback.url}
          {...(view.playback.posterUrl ? { poster: view.playback.posterUrl } : {})}
          title={item.title}
          startAt={view.startAt}
          autoPlay={false}
          onSegment={video.onSegment}
          onPause={video.onStop}
          onEnded={() => { video.onStop(); onEnded(); }}
          onError={(_error, positionSeconds) => video.onError(positionSeconds)}
        />
      ) : null}
      {view.kind === "failed" ? (
        <Unavailable
          title={VIDEO_FAILED}
          description="Try again in a moment."
          retrying={view.retrying}
          retry={() => { trackApp("retry_clicked", { area: "video" }); video.tryAgain(); }}
          focusRef={focusRef}
        />
      ) : null}
    </div>
  );
}
